import { Tooltip } from "@mui/material";
import { twMerge } from "tailwind-merge";

import { Region } from "../../../api/types/regions";
import { Trash } from "../../../components/icons/trash";
import { FarmTreeRoles } from "../../../consts/farm-tree-roles";
import { UserRole } from "../../../consts/user-role";
import { useAuth } from "../../../hooks/useAuth";
import { ApiValidationErrors } from "../../../types/api-error";
import { PriceItem } from "../../../types/price-item";
import { PriceValue } from "../../../types/price-value";
import { PriceUpdatePayload } from "../api/useUpdatePrice";

import { PriceForm } from "./PriceForm";
import { PriceCell } from "./ProductsWithPrices";

export type ProductTableRowProps = {
  item: PriceItem;
  onCellEdit: (data: PriceCell) => void;
  editedCell: PriceCell;
  onRegionSave: (data: Partial<PriceUpdatePayload>) => void;
  onWorldSave: (data: Partial<PriceUpdatePayload>) => void;
  onPriceDelete: (productId: number, regionId: number) => void;
  selectedRegion: Region | undefined | null;
  apiErrors: ApiValidationErrors;
};

export function PricesTableRow({
  item,
  onCellEdit,
  editedCell,
  onRegionSave,
  onWorldSave,
  onPriceDelete,
  selectedRegion,
  apiErrors,
}: ProductTableRowProps) {
  const { user } = useAuth();
  const canDeletePrice = FarmTreeRoles.includes(user?.Role as UserRole);
  const canEditWorldPrice = FarmTreeRoles.includes(user?.Role as UserRole);

  const handleWorldUpdate = (data: PriceValue) => {
    const dataToSave: Partial<PriceUpdatePayload> = {
      data: { ...data },
      productId: item.productId,
    };
    onWorldSave(dataToSave);
  };

  const handleRegionUpdate = (data: PriceValue) => {
    const dataToSave: Partial<PriceUpdatePayload> = {
      data: { ...data },
      productId: item.productId,
    };
    onRegionSave(dataToSave);
  };

  return (
    <div
      className={twMerge(
        "grid grid-cols-4 gap-0 items-center min-h-14 px-4 odd:bg-white even:bg-gray-100",
        selectedRegion && "grid-cols-6",
      )}>
      <div className="col-span-1">{item.name}</div>

      {/* World */}
      <div className="col-span-2">
        <PriceForm
          type="world"
          readOnly={!canEditWorldPrice}
          editedItem={editedCell}
          item={item}
          onSubmit={handleWorldUpdate}
          data={item.world}
          apiErrors={apiErrors}
          onCancel={() => onCellEdit(null)}
          onEdit={value =>
            onCellEdit({ column: value, row: item.productId!, type: "world" })
          }
        />
      </div>

      {/* Region */}
      {selectedRegion && (
        <div className="col-span-2">
          <PriceForm
            type="region"
            onCancel={() => onCellEdit(null)}
            item={item}
            editedItem={editedCell}
            data={item.region}
            apiErrors={apiErrors}
            onSubmit={handleRegionUpdate}
            onEdit={value =>
              onCellEdit({
                column: value,
                row: item.productId!,
                type: "region",
              })
            }
          />
        </div>
      )}

      {/* Actions */}
      <div className="col-span-1 flex items-center justify-center">
        {canDeletePrice &&
          selectedRegion &&
          item.productId &&
          selectedRegion?.Id && (
            <Tooltip title="Restore World prices">
              <div>
                <Trash
                  className="text-darkGray hover:text-red-500 text-xl cursor-pointer"
                  onClick={() => {
                    if (item.productId && selectedRegion?.Id) {
                      onPriceDelete(item.productId, selectedRegion.Id);
                    }
                  }}
                />
              </div>
            </Tooltip>
          )}
      </div>
    </div>
  );
}
